const STORAGE_KEY = 'longPingWatchList';

export interface LongPingWatchEntry {
  cpeId: string;
  cpeName?: string;
}

const readList = (): LongPingWatchEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeList = (list: LongPingWatchEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignore storage failures (private browsing, quota, etc.) — the watch
    // just won't survive a page reload in that case.
  }
};

export const getLongPingWatchList = (): LongPingWatchEntry[] => readList();

export const addLongPingWatch = (cpeId: string, cpeName?: string) => {
  const list = readList().filter((entry) => entry.cpeId !== cpeId);
  list.push({ cpeId, cpeName });
  writeList(list);
};

export const removeLongPingWatch = (cpeId: string) => {
  writeList(readList().filter((entry) => entry.cpeId !== cpeId));
};

// The Long Ping dialog (polling every few seconds while open) and the
// always-mounted background LongPingNotifier (polling independently every
// 15s) can both observe the same test finishing at nearly the same time.
// This shared, synchronous "already notified" guard — keyed by a stable id
// for the specific test run — makes sure only whichever of them gets there
// first actually fires the toast/notification.
const NOTIFIED_KEY = 'longPingNotifiedRuns';
const MAX_NOTIFIED_ENTRIES = 50;

const readNotifiedRuns = (): string[] => {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeNotifiedRuns = (runIds: string[]) => {
  try {
    localStorage.setItem(NOTIFIED_KEY, JSON.stringify(runIds.slice(-MAX_NOTIFIED_ENTRIES)));
  } catch {
    // Ignore storage failures — worst case a run gets notified twice.
  }
};

// Returns true if this call is the one that claimed the notification for this
// run (i.e. it hadn't already been notified), false if someone else already
// claimed it — callers should only fire their toast/notification when true.
export const claimLongPingRunNotification = (runId: string): boolean => {
  const runIds = readNotifiedRuns();
  if (runIds.includes(runId)) return false;
  writeNotifiedRuns([...runIds, runId]);
  return true;
};
